const STORAGE_PREFIX = 'weldAnalytics.job.';
const STORAGE_PREFIX_MULTI = 'weldAnalytics.jobs.';
const STORAGE_PREFIX_SELECTION = 'weldAnalytics.selection.';

export type PipeJobRef = { pipeId: string; jobNumber: string };

export type WeldAnalyticsStoredSelection = {
  pipeIds: string[];
  jobRefs: PipeJobRef[];
};

type PipeSizeShape = {
  pipeId?: number | string;
  jobNumber?: { jobNumbers?: string[] };
};

type SetupShape = { pipeSizes?: PipeSizeShape[] };

/** Stable key so the same job under two pipes stays distinct in the picker. */
export function pipeJobRefKey(ref: PipeJobRef): string {
  return `${ref.pipeId}::${ref.jobNumber}`;
}

export function parsePipeJobRefKey(key: string): PipeJobRef | null {
  const idx = key.indexOf('::');
  if (idx <= 0) return null;
  const pipeId = key.slice(0, idx).trim();
  const jobNumber = key.slice(idx + 2).trim();
  if (!pipeId || !jobNumber) return null;
  return { pipeId, jobNumber };
}

export function uniqueJobNumbersFromRefs(jobRefs: readonly PipeJobRef[]): string[] {
  const set = new Set<string>();
  jobRefs.forEach((r) => {
    const jn = r.jobNumber?.trim();
    if (jn) set.add(jn);
  });
  return Array.from(set).sort();
}

export function uniquePipeIdsFromRefs(jobRefs: readonly PipeJobRef[]): string[] {
  const set = new Set<string>();
  jobRefs.forEach((r) => {
    const id = String(r.pipeId ?? '').trim();
    if (id) set.add(id);
  });
  return Array.from(set);
}

/** All pipe–job pairs for the given pipeIds (keeps per-pipe identity; same job may appear twice). */
export function jobRefsForPipes(
  pipeDetails: Record<string, unknown> | null | undefined,
  pipeIds: readonly (string | number)[] | null | undefined,
): PipeJobRef[] {
  if (!pipeDetails || typeof pipeDetails !== 'object') return [];
  if (!pipeIds?.length) return [];
  const targets = new Set(pipeIds.map((id) => String(id)));
  const seen = new Set<string>();
  const out: PipeJobRef[] = [];

  Object.values(pipeDetails).forEach((setup: unknown) => {
    const s = setup as SetupShape;
    s.pipeSizes?.forEach((pipe) => {
      const pipeId = String(pipe.pipeId ?? '');
      if (!targets.has(pipeId)) return;
      pipe.jobNumber?.jobNumbers?.forEach((jn) => {
        if (!jn || typeof jn !== 'string' || !jn.trim()) return;
        const ref: PipeJobRef = { pipeId, jobNumber: jn.trim() };
        const key = pipeJobRefKey(ref);
        if (seen.has(key)) return;
        seen.add(key);
        out.push(ref);
      });
    });
  });

  return out.sort((a, b) => {
    const byPipe = a.pipeId.localeCompare(b.pipeId);
    return byPipe !== 0 ? byPipe : a.jobNumber.localeCompare(b.jobNumber);
  });
}

/** Drop refs whose pipe is no longer selected. */
export function pruneJobRefs(
  jobRefs: readonly PipeJobRef[],
  pipeIds: readonly (string | number)[],
): PipeJobRef[] {
  if (!pipeIds.length) return [];
  const targets = new Set(pipeIds.map((id) => String(id)));
  return jobRefs.filter((r) => targets.has(String(r.pipeId)));
}

export function jobRefsKey(jobRefs: readonly PipeJobRef[]): string {
  return [...jobRefs]
    .map(pipeJobRefKey)
    .sort()
    .join('|');
}

export function jobNumbersFromPipeDetails(pipeDetails: Record<string, unknown> | null | undefined): string[] {
  if (!pipeDetails || typeof pipeDetails !== 'object') return [];
  const set = new Set<string>();
  Object.values(pipeDetails).forEach((setup: unknown) => {
    const s = setup as SetupShape;
    s.pipeSizes?.forEach((pipe) => {
      pipe.jobNumber?.jobNumbers?.forEach((jn) => {
        if (jn && typeof jn === 'string' && jn.trim()) {
          set.add(jn.trim());
        }
      });
    });
  });
  return Array.from(set).sort();
}

/**
 * Return distinct job numbers configured for a specific pipeId.
 * Used by Weld Analytics so the job-number multi-select is scoped to the pipe
 * the user picked at the AnalyticsShell level (mirrors the NDT dashboard flow).
 */
export function jobNumbersForPipe(
  pipeDetails: Record<string, unknown> | null | undefined,
  pipeId: string | number | null | undefined,
): string[] {
  if (!pipeDetails || typeof pipeDetails !== 'object') return [];
  if (pipeId === null || pipeId === undefined || pipeId === '') return [];
  const target = String(pipeId);
  const set = new Set<string>();
  Object.values(pipeDetails).forEach((setup: unknown) => {
    const s = setup as SetupShape;
    s.pipeSizes?.forEach((pipe) => {
      if (String(pipe.pipeId ?? '') !== target) return;
      pipe.jobNumber?.jobNumbers?.forEach((jn) => {
        if (jn && typeof jn === 'string' && jn.trim()) {
          set.add(jn.trim());
        }
      });
    });
  });
  return Array.from(set).sort();
}

export function getStoredJobNumber(projectId: string | number): string | null {
  try {
    return sessionStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
  } catch {
    return null;
  }
}

export function setStoredJobNumber(projectId: string | number, jobNumber: string): void {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${projectId}`, jobNumber);
  } catch {
    /* ignore */
  }
}

export function getStoredJobNumbers(projectId: string | number, pipeId: string | number | null): string[] {
  try {
    const key = pipeId != null
      ? `${STORAGE_PREFIX_MULTI}${projectId}.${pipeId}`
      : `${STORAGE_PREFIX_MULTI}${projectId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function setStoredJobNumbers(
  projectId: string | number,
  pipeId: string | number | null,
  jobNumbers: string[],
): void {
  try {
    const key = pipeId != null
      ? `${STORAGE_PREFIX_MULTI}${projectId}.${pipeId}`
      : `${STORAGE_PREFIX_MULTI}${projectId}`;
    sessionStorage.setItem(key, JSON.stringify(jobNumbers));
  } catch {
    /* ignore */
  }
}

function isPipeJobRef(v: unknown): v is PipeJobRef {
  if (!v || typeof v !== 'object') return false;
  const r = v as PipeJobRef;
  return typeof r.pipeId === 'string' && typeof r.jobNumber === 'string'
    && Boolean(r.pipeId.trim()) && Boolean(r.jobNumber.trim());
}

export function getStoredSelection(
  projectId: string | number,
): WeldAnalyticsStoredSelection | null {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX_SELECTION}${projectId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const pipeIds = Array.isArray(parsed.pipeIds)
      ? parsed.pipeIds.filter((v: unknown): v is string => typeof v === 'string' && Boolean(v.trim()))
      : [];
    const jobRefs = Array.isArray(parsed.jobRefs)
      ? parsed.jobRefs.filter(isPipeJobRef).map((r: PipeJobRef) => ({
        pipeId: r.pipeId.trim(),
        jobNumber: r.jobNumber.trim(),
      }))
      : [];
    return { pipeIds, jobRefs };
  } catch {
    return null;
  }
}

export function setStoredSelection(
  projectId: string | number,
  selection: WeldAnalyticsStoredSelection,
): void {
  try {
    sessionStorage.setItem(
      `${STORAGE_PREFIX_SELECTION}${projectId}`,
      JSON.stringify({
        pipeIds: selection.pipeIds.map(String),
        jobRefs: selection.jobRefs.map((r) => ({
          pipeId: String(r.pipeId),
          jobNumber: String(r.jobNumber),
        })),
      }),
    );
  } catch {
    /* ignore */
  }
}

export function distinctJobNumbersFromWelds(
  welds: { job_number?: string }[],
  existing: string[],
): string[] {
  const set = new Set(existing);
  welds.forEach((w) => {
    if (w.job_number?.trim()) set.add(w.job_number.trim());
  });
  return Array.from(set).sort();
}

type ProductionJobRow = {
  job_number?: string;
  jobNumber?: string;
  pipeId?: string | number;
  pipe_id?: string | number;
};

/**
 * Build pipe–job refs from production weld rows for the selected pipes.
 * Used when Project Setup has no jobNumbers configured for a pipe (common when
 * Production already has jobs, e.g. Targa Speedway).
 */
export function jobRefsFromProductionRows(
  rows: readonly ProductionJobRow[] | null | undefined,
  pipeIds: readonly (string | number)[] | null | undefined,
): PipeJobRef[] {
  if (!rows?.length || !pipeIds?.length) return [];
  const targets = new Set(pipeIds.map((id) => String(id)));
  const singlePipe = pipeIds.length === 1 ? String(pipeIds[0]) : null;
  const seen = new Set<string>();
  const out: PipeJobRef[] = [];

  rows.forEach((row) => {
    const jobNumber = String(row.job_number ?? row.jobNumber ?? '').trim();
    if (!jobNumber) return;
    const rowPipe = String(row.pipeId ?? row.pipe_id ?? '').trim();
    const pipeId = targets.has(rowPipe) ? rowPipe : singlePipe && !rowPipe ? singlePipe : '';
    if (!pipeId || !targets.has(pipeId)) return;
    const ref: PipeJobRef = { pipeId, jobNumber };
    const key = pipeJobRefKey(ref);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ref);
  });

  return out.sort((a, b) => {
    const byPipe = a.pipeId.localeCompare(b.pipeId);
    return byPipe !== 0 ? byPipe : a.jobNumber.localeCompare(b.jobNumber);
  });
}

/** Merge setup + production refs (setup first; production fills gaps). */
export function mergeJobRefs(
  primary: readonly PipeJobRef[],
  secondary: readonly PipeJobRef[],
): PipeJobRef[] {
  const seen = new Set<string>();
  const out: PipeJobRef[] = [];
  [...primary, ...secondary].forEach((ref) => {
    const key = pipeJobRefKey(ref);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(ref);
  });
  return out.sort((a, b) => {
    const byPipe = a.pipeId.localeCompare(b.pipeId);
    return byPipe !== 0 ? byPipe : a.jobNumber.localeCompare(b.jobNumber);
  });
}
